import { useEffect, useMemo, useRef, useState } from "react";
import {
  useBlocker,
  useMatch,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useMe } from "../api/me";
import { useReshape } from "../api/models";
import type { AssetDetail, Dressing } from "../api/types";
import { ErrorLine } from "../ui/ErrorLine";
import { Button, Card, Dialog, Slider } from "../ui";
import type { ViewerHandle, ViewerPick } from "../viewer/Viewer";
import {
  type PartDoc,
  type SlotDoc,
  type Vec3,
  humanise,
  sizeCm,
} from "../viewer/math";
import { SaveLookDialog } from "./SaveLookDialog";
import { SurfacesPanel } from "./SurfacesPanel";
import { TrueRenderButton } from "./TrueRenderButton";
import {
  INSPECTOR_GRID,
  Inspector,
  InspectorHeader,
  UsedInAds,
  Viewer,
  ViewerSlot,
} from "./inspector";
import { useDressingDetails } from "./useDressingDetails";

const MOUNT_SENTENCES: Record<string, string> = {
  floor: "Stands on the floor or a table",
  wall: "Hangs on a wall",
  ceiling: "Hangs from the ceiling",
};

/** `<slot>:<id>` of a `?skin=` or `?material=` parameter. */
function slotAndId(value: string): [string, string] | null {
  const at = value.indexOf(":");
  if (at < 1 || at === value.length - 1) return null;
  return [value.slice(0, at), value.slice(at + 1)];
}

/** How the page opens: an object as it is, a look with its choices, then
 *  every `?skin=<slot>:<id>` and `?material=<slot>:<id>` of the address, in
 *  the order given. One that names a surface the object does not have is
 *  ignored. */
export function initialDressing(
  detail: AssetDetail,
  params: URLSearchParams,
): Dressing {
  const shape = detail.shape ?? detail;
  const look = detail.kind === "variant" ? detail.meta : {};
  const dressing: Dressing = {
    materials: { ...(look.materials ?? {}) },
    skins: { ...(look.skins ?? {}) },
    colors_hex: { ...(look.colors_hex ?? {}) },
    poses: {},
  };
  const slots = new Set(
    ((shape.meta.slots ?? []) as SlotDoc[]).map((slot) => slot.name),
  );
  for (const value of params.getAll("skin")) {
    const pair = slotAndId(value);
    if (!pair || !slots.has(pair[0])) continue;
    dressing.skins[pair[0]] = pair[1];
    delete dressing.materials[pair[0]];
    delete dressing.colors_hex[pair[0]];
  }
  for (const value of params.getAll("material")) {
    const pair = slotAndId(value);
    if (!pair || !slots.has(pair[0])) continue;
    dressing.materials[pair[0]] = pair[1];
    delete dressing.skins[pair[0]];
  }
  return dressing;
}

/** A value as JSON with every object's keys in order, so two maps holding
 *  the same choices compare equal however they were built. */
function ordered(value: unknown): string {
  return JSON.stringify(value, (_key, inner: unknown) =>
    inner && typeof inner === "object" && !Array.isArray(inner)
      ? Object.fromEntries(
          Object.entries(inner).sort(([a], [b]) => (a < b ? -1 : 1)),
        )
      : inner,
  );
}

/** What a look stores of a dressing. A pose is not part of it: an ad sets a
 *  pose itself. */
const choices = (d: Dressing) =>
  ordered({ materials: d.materials, skins: d.skins, colors_hex: d.colors_hex });

/** A coordinate with a fixed number of decimals, and never "-0.00". */
function fixed(value: number, digits: number): string {
  const text = value.toFixed(digits);
  return Number(text) === 0 ? (0).toFixed(digits) : text;
}

const triple = (v: Vec3, digits: number) =>
  `(${v.map((n) => fixed(n, digits)).join(", ")})`;

/** "Place a logo here": where the user clicked, in the object's own space.
 *  The one place the interface shows metres, because the text is made to be
 *  pasted where the model reads it. */
function LogoPlace({ name, pick }: { name: string; pick: ViewerPick }) {
  const [copied, setCopied] = useState(false);
  const at = triple(pick.point, 3);
  const facing = triple(pick.normal, 2);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(
        `Put the logo on ${name} at ${at}, facing ${facing}.`,
      );
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <Card className="mt-4 flex flex-col gap-2 p-4" data-testid="logo-place">
      <h2 className="tb-h-row m-0">Place a logo here</h2>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="m-0 text-sm text-slate-800" data-testid="logo-at">
          at {at.replace(/-/g, "−")} facing {facing.replace(/-/g, "−")}
        </p>
        <Button
          type="button"
          size="sm"
          className="min-h-[44px] sm:min-h-0"
          onClick={() => void copy()}
        >
          Copy
        </Button>
      </div>
      <p className="m-0 text-sm text-slate-600">
        Paste this into your brief or into an ad&apos;s change box. The ad uses
        its brand kit&apos;s logo, or an image you attached to it.
      </p>
      {copied ? (
        <p role="status" className="m-0 text-sm text-slate-700">
          Copied.
        </p>
      ) : null}
    </Card>
  );
}

export const MAKE_MY_OWN = "Make my own version";
export const OPEN_IN_STUDIO = "Open in the design studio";
export const NOT_RESHAPED = "This object can be dressed but not reshaped.";

/** Reshaping ([library-section §5.4], [design-studio §13.5]): an object that
 *  carries its generator opens in the design studio, a house-made one as a
 *  design of the user's own, one the organisation published as the design it
 *  came from. Any other object can only be dressed. */
function Reshaping({
  shape,
}: {
  shape: Pick<AssetDetail, "id" | "tier" | "origin" | "has_generator">;
}) {
  const navigate = useNavigate();
  const reshape = useReshape();
  const label = !shape.has_generator
    ? null
    : shape.tier === "curated"
      ? MAKE_MY_OWN
      : shape.origin === "studio"
        ? OPEN_IN_STUDIO
        : null;
  if (label === null) {
    return (
      <p className="m-0 text-sm text-slate-700" data-testid="reshaping">
        {NOT_RESHAPED}
      </p>
    );
  }
  return (
    <section className="flex flex-col gap-2" data-testid="reshaping">
      <div>
        <Button
          type="button"
          className="min-h-[44px] disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
          disabled={reshape.isPending}
          aria-busy={reshape.isPending ? true : undefined}
          onClick={() =>
            reshape.mutate(shape.id, {
              onSuccess: (design) =>
                navigate(`/assets/studio/${encodeURIComponent(design.id)}`),
            })
          }
        >
          {label}
        </Button>
      </div>
      {reshape.error ? (
        <ErrorLine error={reshape.error} context="remix" />
      ) : null}
    </section>
  );
}

function ShapeInspector(props: {
  detail: AssetDetail;
  onExpired(): void;
}): JSX.Element {
  const { detail } = props;
  const shape = detail.shape ?? detail;
  const [params] = useSearchParams();
  // Read once, when the detail first arrives: the address dresses the object
  // as the page opens and is not applied again after the user changes it.
  const [dressing, setDressing] = useState<Dressing>(() =>
    initialDressing(detail, params),
  );
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [pick, setPick] = useState<ViewerPick | null>(null);
  const viewerRef = useRef<ViewerHandle>(null);

  const features = useMe().data?.features;
  const stills = features?.stills === true;
  // The page's own object: a look is rendered as a look, with what the user
  // changed on it.
  const stillTarget = useMemo(
    () => ({ kind: detail.kind, id: detail.id }),
    [detail.kind, detail.id],
  );

  const details = useDressingDetails(detail, dressing);
  const shapeId = shape.id;
  const shapeVersion = shape.version;
  const shapeMeta = shape.meta;
  const shapeFiles = shape.files;
  const shapeViewer = shape.viewer;
  const subject = useMemo(
    () => ({
      kind: "shape" as const,
      detail: {
        id: shapeId,
        version: shapeVersion,
        meta: shapeMeta,
        files: shapeFiles,
        viewer: shapeViewer,
      },
    }),
    [shapeId, shapeVersion, shapeMeta, shapeFiles, shapeViewer],
  );
  // The panel reads the object with everything this page already holds: the
  // object's own defaults and, on a look's page, the look's choices.
  const ownDefaults = detail.defaults;
  const shapeDefaults = shape.defaults;
  const panelDetail = useMemo(
    () => ({
      id: shapeId,
      meta: shapeMeta,
      defaults: {
        materials: { ...shapeDefaults.materials, ...ownDefaults.materials },
        skins: { ...shapeDefaults.skins, ...ownDefaults.skins },
      },
    }),
    [shapeId, shapeMeta, shapeDefaults, ownDefaults],
  );

  // Unsaved changes: the choices differ from the page as it is stored (an
  // object as it is, a look with its own choices). What the address put on
  // counts as a change, so it can be saved.
  const stored = useMemo(
    () => choices(initialDressing(detail, new URLSearchParams())),
    [detail],
  );
  const dirty = choices(dressing) !== stored;

  const navigate = useNavigate();
  // Set just before a navigation that must not ask. A state change would be
  // too late: the blocker's function is replaced only after the next render.
  const leaving = useRef(false);
  const blocker = useBlocker(() => dirty && !leaving.current);
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (window.confirm("Leave without saving this look?")) blocker.proceed();
    else blocker.reset();
  }, [blocker]);
  // Closing the tab or reloading asks too, in the browser's own words.
  useEffect(() => {
    if (!dirty) return;
    const ask = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", ask);
    return () => window.removeEventListener("beforeunload", ask);
  }, [dirty]);

  const [saving, setSaving] = useState<"look" | "use" | null>(null);
  const [asking, setAsking] = useState(false);
  const here = `/new?pin=${detail.kind}:${encodeURIComponent(detail.id)}`;
  const go = (to: string, state?: { saved: true }) => {
    leaving.current = true;
    navigate(to, state ? { state } : undefined);
  };

  const select = (slot: string | null) => {
    setSelectedSlot(slot);
    // The logo block goes when nothing is selected any more.
    if (slot === null) setPick(null);
  };

  const meta = shape.meta;
  const name = String(meta.name ?? "");
  const parts = ((meta.parts ?? []) as PartDoc[]).filter(
    (part) => part.range_deg,
  );
  const mount = MOUNT_SENTENCES[String(meta.mount)];

  return (
    <>
      <InspectorHeader
        detail={detail}
        onDeleting={() => {
          leaving.current = true;
        }}
      />
      <div className={INSPECTOR_GRID}>
        <div>
          <ViewerSlot>
            <Viewer
              ref={viewerRef}
              subject={subject}
              dressing={dressing}
              details={details}
              selectedSlot={selectedSlot}
              marker={pick?.point ?? null}
              onPick={(next) => {
                setSelectedSlot(next.slot);
                setPick(next);
              }}
              onExpired={props.onExpired}
            />
          </ViewerSlot>
          {pick ? <LogoPlace name={name} pick={pick} /> : null}
        </div>
        <div className="flex flex-col gap-6">
          <section className="flex flex-col gap-1" data-testid="facts">
            {Array.isArray(meta.size_m) ? (
              <p className="m-0 text-sm font-semibold text-slate-800">
                {sizeCm(meta.size_m as Vec3)}
              </p>
            ) : null}
            {mount ? (
              <p className="m-0 text-sm text-slate-700">{mount}</p>
            ) : null}
            {typeof meta.triangles === "number" ? (
              <details className="text-sm text-slate-700">
                <summary className="min-h-[44px] cursor-pointer py-2 sm:min-h-0 sm:py-0">
                  More
                </summary>
                Detail: {meta.triangles.toLocaleString("en-US")} triangles
              </details>
            ) : null}
          </section>
          {parts.length > 0 ? (
            <section className="flex flex-col gap-2" data-testid="moving-parts">
              <h2 className="tb-h-row m-0">Moving parts</h2>
              {parts.map((part) => (
                <Slider
                  key={part.name}
                  label={humanise(part.name)}
                  min={part.range_deg![0]}
                  max={part.range_deg![1]}
                  step={1}
                  unit="°"
                  value={dressing.poses[part.name] ?? part.rest_deg ?? 0}
                  onChange={(deg) =>
                    setDressing((now) => ({
                      ...now,
                      poses: { ...now.poses, [part.name]: deg },
                    }))
                  }
                />
              ))}
            </section>
          ) : null}
          <SurfacesPanel
            detail={panelDetail}
            dressing={dressing}
            onChange={setDressing}
            selectedSlot={selectedSlot}
            onSelect={select}
          />
          <section className="flex flex-wrap gap-2" data-testid="look-actions">
            <Button
              type="button"
              variant="primary"
              className="min-h-[44px] disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
              disabled={!dirty}
              onClick={() => setSaving("look")}
            >
              Save this look
            </Button>
            {detail.kind === "variant" && detail.stale ? null : (
              <Button
                type="button"
                className="min-h-[44px] sm:min-h-0"
                onClick={() => (dirty ? setAsking(true) : go(here))}
              >
                Use in a new ad
              </Button>
            )}
          </section>
          {stills ? (
            <TrueRenderButton
              target={stillTarget}
              dressing={dressing}
              poses={dressing.poses}
              viewerRef={viewerRef}
            />
          ) : null}
          {features?.studio === true ? <Reshaping shape={shape} /> : null}
        </div>
      </div>
      <SaveLookDialog
        open={saving !== null}
        onClose={() => setSaving(null)}
        shape={shape}
        dressing={dressing}
        viewerRef={viewerRef}
        onSaved={(id) =>
          saving === "use"
            ? go(`/new?pin=variant:${encodeURIComponent(id)}`)
            : go(`/assets/variant/${encodeURIComponent(id)}`, { saved: true })
        }
      />
      <Dialog
        open={asking}
        onClose={() => setAsking(false)}
        title="Use it as you dressed it?"
        footer={
          <>
            <Button type="button" onClick={() => go(here)}>
              Use the original
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                setAsking(false);
                setSaving("use");
              }}
            >
              Save this look and use it
            </Button>
          </>
        }
      >
        <p className="m-0 text-sm text-slate-700">
          You changed how this object is dressed. Save it as a look to use it
          that way in the ad.
        </p>
      </Dialog>
      <UsedInAds detail={detail} />
    </>
  );
}

/** `/assets/shape/:id` and `/assets/variant/:id`: an object, or a saved look
 *  of one, in 3D, with its facts, its moving parts and its surfaces. The
 *  kind is read from the path. */
export function AssetShapePage() {
  const { id = "" } = useParams();
  const kind = useMatch("/assets/variant/:id") ? "variant" : "shape";
  return (
    <Inspector kind={kind} id={id}>
      {(detail, asset) => (
        <ShapeInspector
          key={`${detail.kind}:${detail.id}`}
          detail={detail}
          onExpired={asset.onExpired}
        />
      )}
    </Inspector>
  );
}
